export interface NpmStats {
  version: string;
  weeklyDownloads: number;
  monthlyDownloads: number;
  lastPublished: string;
  license: string;
  maintainersCount: number;
}

export async function fetchNpmStats(packageName: string): Promise<NpmStats> {
  const cacheKey = `npm-stats-${packageName}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache for 12 hours
      if (Date.now() - timestamp < 12 * 60 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {
    // Ignore storage errors
  }

  // Fetch registry data
  const registryUrl = `https://registry.npmjs.org/${packageName}`;
  const registryResponse = await fetch(registryUrl);
  if (!registryResponse.ok) {
    throw new Error(`Package ${packageName} not found on registry.`);
  }

  const registryData = await registryResponse.json();
  const latestVersion = registryData['dist-tags']?.latest || '';
  const time = registryData.time || {};
  const lastPublished = time[latestVersion] || time.modified || '';
  const license = registryData.license || registryData.versions?.[latestVersion]?.license || 'MIT';
  const maintainersCount = registryData.maintainers?.length || 1;

  // Fetch downloads data
  let weeklyDownloads = 0;
  let monthlyDownloads = 0;

  try {
    const weeklyUrl = `https://api.npmjs.org/downloads/point/last-week/${packageName}`;
    const weeklyResponse = await fetch(weeklyUrl);
    if (weeklyResponse.ok) {
      const data = await weeklyResponse.json();
      weeklyDownloads = data.downloads || 0;
    }

    const monthlyUrl = `https://api.npmjs.org/downloads/point/last-month/${packageName}`;
    const monthlyResponse = await fetch(monthlyUrl);
    if (monthlyResponse.ok) {
      const data = await monthlyResponse.json();
      monthlyDownloads = data.downloads || 0;
    }
  } catch (err) {
    console.warn(`Could not fetch download stats for ${packageName}`, err);
  }

  const stats: NpmStats = {
    version: latestVersion,
    weeklyDownloads,
    monthlyDownloads,
    lastPublished,
    license,
    maintainersCount
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify({
      data: stats,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore storage errors
  }

  return stats;
}
